import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import GetCrudPermissions from "components/common/services/RolesAndPermissions/GetCrudPermissions";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EditIcon from "../../../../static/images/EditIcon";
import { ApiCall } from "components/common/services/ApiServices";
import { MANAGE_CLOSURES, DELETE_CLOSURE_DETAILS } from "routes/ApiEndpoints";
import Title from "components/common/atoms/Title";
import {
  t,
  translate,
} from "components/CentralDataMangement/translation/Translation";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import PaginationNew from "components/common/atoms/PaginationNew";
import SpinnerWrapper from "components/common/atoms/SpinnerWrapper";
import NoRecords from "components/common/atoms/NoRecords";
import SearchFields from "components/common/atoms/SearchFields";
import ResetBtn from "components/common/atoms/ResetBtn";
import Sorting from "components/common/atoms/Sorting";
import ActionIconWrapper from "components/common/atoms/ActionIconWrapper";
import Button from "components/common/atoms/Button";
import ClosurePopup from "../ClosurePopup";
import { createFilterConfig } from "./Filters";
import * as CONST from "../../../../Constants";
import ArchiveNew from "static/images/ArchiveNew";
import CustomNotify from "components/common/atoms/CustomNotify";
import ArchiveModalPopup from "components/CompanyCreation/Company/manageCompany/ArchiveModalPopup";
import { ClosureData, PaginationData, SearchProps, defaultSearchData} from "./Interface";
import NavBar from "components/common/atoms/NavBar";
import { getQueryParamsFromRedux, createQueryParams } from "components/common/utlis/HelperFunctions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { setDestinationPath, clearDestinationPath } from "features/NavigationSlice";
import GetQueryParams from "components/common/services/GetQueryParams";
import UnArchive from "static/images/UnArchive";

export const ManageClosureDetails: React.FC = () => {
  const dispatch = useDispatch();
  const canCompany = GetCrudPermissions("Company");

  const destinationPath = useSelector(
    (state: RootState) => state.navigation.destinationPath
  );

  const getUrlParams = GetQueryParams();
  const paramFilter = getUrlParams.get("filter");
  const queryParams = getQueryParamsFromRedux(destinationPath, paramFilter);
  const [loading, setLoading] = useState(false);
  const [closures, setClosures] = useState<ClosureData[]>([]);
  const [paginationData, setPaginationData] = useState<PaginationData>({
    totalPages: 1,
    totalRecords: 0,
  });
  // Dropdown options are static; fetch them once and reuse on later searches.
  const dropdownLoadedRef = useRef(false);
  const [dropdownList, setDropdownList] = useState({
    closingTypes: [],
  });
  const [searchData, setSearchData] = useState<SearchProps>(() => {
    return {
      ...defaultSearchData,
      ...queryParams,
    };
  });
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedClosure, setSelectedClosure] = useState<ClosureData | null>(
    null
  );
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveClosureId, setArchiveClosureId] = useState<number | undefined>();
  const [isUnarchive, setIsUnarchive] = useState(false);

  const filters = useMemo(() => {
    return createFilterConfig(dropdownList);
  }, [dropdownList]);

  const defaultTabData = {
    tabFieldName: "closureStatus",
    tabFieldValue: 1, // default to "All" active closures
  };

  // Initialize activeTab from query params
  const [activeTab, setActiveTab] = useState(() => {
    return {
      tabFieldName: "closureStatus",
      tabFieldValue: queryParams.closureStatus ?? 1
    };
  });

  // Fetch closure details with useCallback to prevent recreation
  const fetchClosureDetails = useCallback(async (filterData: SearchProps) => {
    setLoading(true);
    try {
      const data = {
        search: filterData,
        loadDropdown: !dropdownLoadedRef.current,
      };

      const response = await ApiCall.service(
        MANAGE_CLOSURES,
        "POST",
        data,
        false,
        CONST.COMPANY_MICROSERVICE
      );

      if (response?.status === 200 || response?.data?.status === 200) {
        const responseData = response.data?.data ? response.data : response;

        setClosures(responseData.data || []);
        setPaginationData(
          responseData.pagination || { totalPages: 1, totalRecords: 0 }
        );
        if (responseData.dropdownData) {
          setDropdownList(responseData.dropdownData);
          dropdownLoadedRef.current = true;
        }
      }
    } catch (error) {
      console.error("Error fetching closures:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const navItems = [
    { label: "All", fieldName: "closureStatus", value: 1 }, // status = 1
    { label: "Archived", fieldName: "closureStatus", value: 0 }, // status = 0
  ];

  // Effect to fetch data when searchData changes (with debounce for text inputs)
  useEffect(() => {
    const filterData = { ...searchData }; // activeTab values are already in searchData
    const searchUrl = createQueryParams(filterData);
    dispatch(setDestinationPath(searchUrl));

    const delayDebounceFn = setTimeout(() => {
      fetchClosureDetails(filterData);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchData, fetchClosureDetails, dispatch]);

  // Modal handlers
  const handleCreateClick = () => {
    setModalMode("create");
    setSelectedClosure(null);
    setShowModal(true);
  };

  const handleEditClick = (closure: ClosureData) => {
    setModalMode("edit");
    setSelectedClosure(closure);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedClosure(null);
  };

  const handleModalSave = () => {
    // Refresh the table data after save
    fetchClosureDetails(searchData);
  };

  // Search and filter handlers
  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      [name]: value,
    }));
  };

  const handleSelectChange = (selectedOption: any, fieldName: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      [fieldName]: selectedOption,
    }));
  };

  const handleDateChange = (date: Date | null, fieldName: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      [fieldName]: date,
    }));
  };

  const handleReset = () => {
    dispatch(clearDestinationPath());
    setSearchData(defaultSearchData);
    setActiveTab(defaultTabData);
  };

  // Pagination handlers
  const handlePageChange = (pageNumber: number) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: pageNumber,
    }));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setSearchData((prev) => ({
      ...prev,
      recordsPerPage: newItemsPerPage,
      currentPage: 1,
    }));
  };

  // Sorting handler
  const handleOrderByColumn = (column: string) => {
    let newOrder: "" | "asc" | "desc" = "";
    let newColumn = column;

    if (searchData.orderByColumn === column) {
      if (searchData.orderIn === "") {
        newOrder = "asc";
      } else if (searchData.orderIn === "asc") {
        newOrder = "desc";
      } else {
        newOrder = "";
        newColumn = "";
      }
    } else {
      newOrder = "asc";
    }

    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      orderByColumn: newColumn,
      orderIn: newOrder,
    }));
  };

  // Archive handler
  const handleArchive = (id: number | undefined,unarchive: boolean = false) => {
    setArchiveClosureId(id);
    setIsUnarchive(unarchive);
    setShowArchiveModal(true);
  };

  const handleUnarchive = (id: number | undefined) => {
    const closure = closures.find((c) => c.id === id);
    setArchiveClosureId(id);
    setIsUnarchive(true);
    setShowArchiveModal(true);
  };

  const handleCloseArchiveModal = () => {
    setShowArchiveModal(false);
    setArchiveClosureId(undefined);
  };

  const handleConfirmArchive = async (reason: string = "") => {
    setShowArchiveModal(false);
    if (archiveClosureId) {
      const data = {
        id: archiveClosureId,
        reason: reason,
        isUnarchive: isUnarchive,
      };

      const response = await ApiCall.service(
        DELETE_CLOSURE_DETAILS,
        "POST",
        data,
        false,
        CONST.COMPANY_MICROSERVICE
      );

      if (response?.status === 200) {
        setClosures((prevData) =>
          prevData.filter((closure) => closure.id !== archiveClosureId)
        );
        CustomNotify({
          type: "success",
          message: response.message 
        });
      }
    }
  };

  const handleNavItemClick = (tabFieldName: string, tabFieldValue: any) => {
    setActiveTab({ tabFieldName, tabFieldValue });
    setSearchData((prev) => ({
      ...prev,
      closureStatus: tabFieldValue,
      currentPage: 1,
    }));
  };

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Admin fiche",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <div className="main-container">
        <Title title={t("Closures")} />
        <div className="search-bar">
          <div className="row">
            <div className="col-md-12 company-tabs">
              <NavBar
                navItems={navItems}
                onNavItemClick={handleNavItemClick}
                className="custom-navbar"
                activeItem={activeTab}
              />
            </div>
          </div>
        </div>
        <div className="form-height-dashboard">
          <div className="position-relative tableMainWrapper">
            <div className="row">
              <div className="ManageCreateBtn">
                <AccessControl
                  requiredPermissions={[
                    {
                      permission: "Admin fiche",
                      create: true,
                    },
                  ]}
                >
                  <Button
                    handleClick={handleCreateClick}
                    className="form-button"
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    {t("Create closure")}
                  </Button>
                </AccessControl>
              </div>
            </div>

            <div className="table-responsive tableSection">
              <table className="table manageClosures border-0 mb-0">
                <thead>
                  <tr className="tableSearchFields">
                    <SearchFields
                      searchData={searchData}
                      filters={filters}
                      handleSearchChange={handleSearchChange}
                      handleSelectChange={handleSelectChange}
                      handleDateChange={handleDateChange}
                    />
                    <th className="border-0">
                      <div className="d-flex align-items-center w-100">
                        <ResetBtn handleResetClick={handleReset} />
                      </div>
                    </th>
                  </tr>
                </thead>
              </table>

              <div className="tableContainer scrollBarDesign">
                <table className="table table-hover manageClosures">
                  <thead>
                    <tr className="TableHeader">
                      <th onClick={() => handleOrderByColumn("company_name")}>
                        {t("Company name")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "company_name"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th onClick={() => handleOrderByColumn("start_date")}>
                        {t("Start date")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "start_date"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th onClick={() => handleOrderByColumn("end_date")}>
                        {t("End date")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "end_date"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th
                        onClick={() => handleOrderByColumn("closing_type_name")}
                      >
                        {t("Closure type")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "closing_type_name"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th>{t("Info")}</th>
                      <th>
                        <AccessControl
                          requiredPermissions={[
                            {
                              permission: "Admin fiche",
                              update: true,
                            },
                          ]}
                          actions={true}
                        >
                          {t("Action")}
                        </AccessControl>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <SpinnerWrapper headerLength={6} />
                    ) : (
                      <>
                        {closures && closures.length > 0 ? (
                          closures.map((closure) => (
                            <tr
                              key={closure.id}
                              className="border-bottom align-middle"
                            >
                              <td>
                                {canCompany?.read && closure.company_id ? (
                                  <Link
                                    to={`/company/corner/${closure.company_id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {closure.company_name}
                                  </Link>
                                ) : (
                                  closure.company_name
                                )}
                              </td>
                              <td>{closure.start_date}</td>
                              <td>{closure.end_date}</td>
                              <td>{closure.closing_type_name}</td>
                              <td>{closure.info || "-"}</td>
                              <td className="table-action-icons">
                                <AccessControl
                                  requiredPermissions={[
                                    {
                                      permission: "Admin fiche",
                                      update: true,
                                    },
                                  ]}
                                  actions={true}
                                  strict={false}
                                >
                                  <ActionIconWrapper initialVisibleCount={4}>
                                    {closure.status === 1 && (
                                      <AccessControl
                                        requiredPermissions={[
                                          {
                                            permission: "Admin fiche",
                                            update: true,
                                          },
                                        ]}
                                      >
                                        <button
                                          onClick={() => handleEditClick(closure)}
                                          title={t("Edit")}
                                          className="btn p-0 border-0"
                                        >
                                          <EditIcon />
                                        </button>
                                      </AccessControl>
                                    )}
                                    <AccessControl
                                      requiredPermissions={[
                                        {
                                          permission: "Admin fiche",
                                          delete: true,
                                        },
                                      ]}
                                    >
                                      {closure.status === 1 ? (
                                        <div
                                          onClick={() =>
                                            handleArchive(closure.id, false)
                                          }
                                          title={t("Archive")}
                                          className="d-inline-block cursor-pointer"
                                        >
                                          <ArchiveNew />
                                        </div>
                                      ) : (
                                        <div
                                          onClick={() =>
                                            handleUnarchive(closure.id)
                                          }
                                          title={t("Unarchive")}
                                          className="d-inline-block cursor-pointer"
                                        >
                                          <UnArchive />
                                        </div>
                                      )}
                                    </AccessControl>
                                  </ActionIconWrapper>
                                </AccessControl>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <NoRecords headerLength={6} />
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {!loading && (
                <div className="pagination d-block">
                  <PaginationNew
                    currentPage={searchData.currentPage}
                    totalPages={paginationData.totalPages}
                    totalRecords={paginationData.totalRecords}
                    recordsPerPage={searchData.recordsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="col-md-6 align-self-center"
          style={{ padding: "1vw 0" }}
        >
          <Link to="/dashboard" className="back-btn text-decoration-underline">
            {t("Back")}
          </Link>
        </div>

        <ClosurePopup
          show={showModal}
          onClose={handleModalClose}
          onSave={handleModalSave}
          mode={modalMode}
          closureData={selectedClosure}
          closingTypesList={dropdownList.closingTypes}
        />

        <ArchiveModalPopup
          show={showArchiveModal}
          onHide={handleCloseArchiveModal}
          title={
            isUnarchive
              ? t("Unarchive confirmation")
              : t("Archive confirmation")
          }
          onConfirm={handleConfirmArchive}
          onCancel={handleCloseArchiveModal}
          isUnarchive={isUnarchive}
          existingReason={
            isUnarchive
              ? closures.find((c) => c.id === archiveClosureId)
                  ?.archive_reason || null
              : null
          }
        />
      </div>
    </AccessControl>
  );
};

export default translate(ManageClosureDetails);
